/*
 * AIKO — Elección de medio de pago antes del checkout.
 *
 * Dos botones: tarjeta y transferencia. Elegir acá (y no en el checkout) es lo que permite
 * aplicar el descuento por transferencia ANTES de la pantalla de pago, y marcar las líneas del
 * carrito con una propiedad oculta para que PayMix pueda esconder Mercado Pago.
 *
 * MODO SOLO TRANSFERENCIA
 * Los productos con el tag `solo-transferencia` (cremas: precio fijo, margen chico) no admiten
 * descuento ni cuotas. Ahí queda un solo botón, a precio de lista y SIN cupón. El modo lo decide
 * Liquid (atributo data-transfer-only) y además puede activarse en caliente: si mientras la página
 * está abierta entra al carrito un producto con el tag, todo el carrito pasa a transferencia.
 * Ese cambio llega por el flag que escribe `sections/cart-icon-bubble.liquid`, que es la sección
 * que Dawn re-renderiza en cada modificación del carrito.
 *
 * Escrito en ES5+ conservador a propósito (sin campos privados `#`, sin `??`): la tienda es
 * mobile-first en Argentina y hay iPhones viejos en el parque de dispositivos.
 *
 * Portado desde la implementación de Lumora (tema Horizon) a Dawn: acá no existen
 * `@theme/component`, `ThemeEvents` ni `Theme.routes`, así que las rutas llegan por data-attribute
 * y los eventos de variante se escuchan de tres formas distintas (ver _bindVariantUpdates).
 */
(function () {
  'use strict';

  var PROPERTY = '_medio_pago';
  var TRANSFER_VALUE = 'transferencia';

  /* Cada pasada deja una línea resuelta, así que esto solo protege contra un carrito que crezca
   * mientras el flujo corre. */
  var MAX_LINE_PASSES = 60;
  var RESTORE_ATTEMPTS = 3;

  var CART_FLAG_SELECTOR = '[data-aiko-cart-only]';
  var BUBBLE_ID = 'cart-icon-bubble';

  function cartHasTransferOnly() {
    var flag = document.querySelector(CART_FLAG_SELECTOR);
    if (!flag) return null;
    return flag.getAttribute('data-aiko-cart-only') === 'true';
  }

  function AikoPaymentChoice() {
    return Reflect.construct(HTMLElement, [], AikoPaymentChoice);
  }

  AikoPaymentChoice.prototype = Object.create(HTMLElement.prototype);
  AikoPaymentChoice.prototype.constructor = AikoPaymentChoice;
  Object.setPrototypeOf(AikoPaymentChoice, HTMLElement);

  var proto = AikoPaymentChoice.prototype;

  proto.connectedCallback = function () {
    if (this._ready) return;
    this._ready = true;
    this._pending = false;

    this.cardButton = this.querySelector('[data-aiko-pay-card]');
    this.transferButton = this.querySelector('[data-aiko-pay-transfer]');
    this.cardPrice = this.querySelector('[data-aiko-pay-card-price]');
    this.transferPrice = this.querySelector('[data-aiko-pay-transfer-price]');
    this.transferLabel = this.querySelector('[data-aiko-pay-transfer-label]');
    this.transferFlag = this.querySelector('[data-aiko-pay-flag]');
    this.transferOff = this.querySelector('[data-aiko-pay-off]');
    this.transferNote = this.querySelector('[data-aiko-pay-note]');
    this.hint = this.querySelector('[data-aiko-pay-hint]');
    this.alert = this.querySelector('[data-aiko-pay-alert]');
    this.errorMessage = this.querySelector('[data-aiko-pay-error]');

    /* El botón de tarjeta no existe en modo solo transferencia, así que el componente tiene que
     * funcionar sin él. */
    if (!this.transferButton) return;

    var self = this;
    if (this.cardButton) {
      this.cardButton.addEventListener('click', function (event) {
        event.preventDefault();
        self.handleCard();
      });
    }
    this.transferButton.addEventListener('click', function (event) {
      event.preventDefault();
      self.handleTransfer();
    });

    this._bindVariantUpdates();
    this._bindCartFlag();
  };

  /* ---------------------------------------------------------------- contexto */

  proto._isProductContext = function () {
    return this.getAttribute('data-context') === 'product';
  };

  proto._isTransferOnly = function () {
    return this.getAttribute('data-transfer-only') === 'true';
  };

  proto._route = function (name, fallback) {
    return this.getAttribute('data-route-' + name) || fallback;
  };

  proto._discountCode = function () {
    return this.getAttribute('data-discount-code') || '';
  };

  proto._checkoutUrl = function () {
    return this.getAttribute('data-checkout-url') || '/checkout';
  };

  /* El form puede estar afuera del componente (barra sticky, hero), así que se referencia por
   * selector. Si no hay selector, se busca el form que lo contenga. */
  proto._form = function () {
    var selector = this.getAttribute('data-form-selector');
    var form = selector ? document.querySelector(selector) : this.closest('form');
    return form instanceof HTMLFormElement ? form : null;
  };

  /* ---------------------------------------------------------------- acciones */

  proto.handleCard = function () {
    var self = this;
    if (this._isTransferOnly()) return Promise.resolve();

    return this._run(this.cardButton, function () {
      var chain = Promise.resolve();

      if (self._isProductContext()) {
        chain = chain.then(function () {
          return self._addToCart(null);
        });
      }

      return chain
        .then(function () {
          return self._clearEveryLine();
        })
        .then(function () {
          return self._dropTransferDiscount();
        })
        .then(function () {
          window.location.assign(self._checkoutUrl());
        });
    });
  };

  proto.handleTransfer = function () {
    var self = this;
    return this._run(this.transferButton, function () {
      var chain = Promise.resolve();

      if (self._isProductContext()) {
        chain = chain.then(function () {
          return self._addToCart(TRANSFER_VALUE);
        });
      }

      return chain
        .then(function () {
          return self._markEveryLine(TRANSFER_VALUE);
        })
        .then(function () {
          /*
           * Solo transferencia: precio de lista, sin cupón. Y si el cupón quedó aplicado de antes
           * (la clienta cargó un producto normal por transferencia y después agregó una crema), hay
           * que sacarlo: en un carrito mixto no va descuento a nada.
           */
          if (self._isTransferOnly()) {
            return self._dropTransferDiscount().then(function () {
              window.location.assign(self._checkoutUrl());
            });
          }

          var code = encodeURIComponent(self._discountCode());
          var redirect = encodeURIComponent(self._checkoutUrl());
          window.location.assign('/discount/' + code + '?redirect=' + redirect);
        });
    });
  };

  /*
   * Mantiene los botones deshabilitados mientras corre la cadena de pedidos, para que un segundo
   * clic no arranque un flujo paralelo. Si sale bien navegamos a otra página, así que los botones
   * quedan deshabilitados a propósito.
   */
  proto._run = function (button, action) {
    if (this._pending) return Promise.resolve();

    var self = this;
    this._pending = true;
    this._setDisabled(true);
    button.setAttribute('aria-busy', 'true');
    this.setAttribute('data-loading', '');
    if (this.errorMessage) this.errorMessage.hidden = true;

    return Promise.resolve()
      .then(action)
      .catch(function (error) {
        console.error('[aiko-payment-choice]', error);

        self._pending = false;
        self._setDisabled(false);
        button.removeAttribute('aria-busy');
        self.removeAttribute('data-loading');
        if (self.errorMessage) self.errorMessage.hidden = false;
      });
  };

  /* En modo solo transferencia el botón de tarjeta queda deshabilitado para siempre, así que nunca
   * se vuelve a habilitar desde acá. */
  proto._setDisabled = function (value) {
    if (this.cardButton && !this._isTransferOnly()) this.cardButton.disabled = value;
    this.transferButton.disabled = value;
  };

  /* ------------------------------------------------- flag de carrito en vivo */

  /*
   * `sections/cart-icon-bubble.liquid` publica si el carrito tiene alguna línea con el tag. Dawn
   * re-renderiza esa sección en cada alta de producto, así que observar su contenido alcanza para
   * enterarse sin volver a pedir nada al server (y `/cart.js` no trae los tags del producto).
   *
   * Solo se pasa a solo-transferencia, nunca al revés: en una ficha de producto el carrito solo
   * puede crecer. Para quitar una crema hay que ir al carrito, que se renderiza de nuevo en el
   * server con el estado correcto.
   */
  proto._bindCartFlag = function () {
    var self = this;
    var bubble = document.getElementById(BUBBLE_ID);
    if (!bubble || typeof MutationObserver !== 'function') return;

    this._syncFromCart();

    var observer = new MutationObserver(function () {
      self._syncFromCart();
    });
    observer.observe(bubble, { childList: true, subtree: true });
  };

  proto._syncFromCart = function () {
    if (this._isTransferOnly()) return;
    if (cartHasTransferOnly() !== true) return;
    this._enterTransferOnly();
  };

  proto._enterTransferOnly = function () {
    this.setAttribute('data-transfer-only', 'true');
    this.classList.add('aikopay--only');

    if (this.cardButton) {
      this.cardButton.disabled = true;
      this.cardButton.hidden = true;
      this.cardButton.setAttribute('aria-hidden', 'true');
    }

    if (this.transferFlag) this.transferFlag.hidden = true;
    if (this.transferOff) this.transferOff.hidden = true;
    if (this.transferNote) this.transferNote.hidden = true;
    if (this.hint) this.hint.hidden = false;
    if (this.alert) this.alert.hidden = false;

    if (this.transferLabel) {
      var label = this.getAttribute('data-only-label');
      if (label) this.transferLabel.textContent = label;
    }

    /* El importe deja de tener el 15% descontado. */
    if (this._isProductContext()) {
      var variantId = this.getAttribute('data-variant-id');
      if (variantId) this._renderPrices(variantId);
    } else if (this.transferPrice) {
      var listPrice = this.getAttribute('data-list-price');
      if (listPrice) this.transferPrice.textContent = listPrice;
    }
  };

  /* ---------------------------------------------------------------- carrito */

  proto._addToCart = function (paymentMethod) {
    var form = this._form();
    var body = form ? new FormData(form) : new FormData();

    if (!form) {
      var variantId = this.getAttribute('data-variant-id');
      if (!variantId) {
        return Promise.reject(new Error('aiko-payment-choice: sin form de producto ni variant id'));
      }
      body.set('id', variantId);
      body.set('quantity', '1');
    }

    /* El botón de tarjeta NO manda la propiedad, pero el form puede traerla si otro script la
     * dejó puesta. Se limpia siempre y se vuelve a setear solo si corresponde. */
    body.delete('properties[' + PROPERTY + ']');
    if (paymentMethod) body.set('properties[' + PROPERTY + ']', paymentMethod);

    return fetch(this._route('add', '/cart/add'), {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body: body,
    }).then(function (response) {
      if (!response.ok) {
        return response
          .text()
          .catch(function () {
            return '';
          })
          .then(function (text) {
            throw new Error('aiko-payment-choice: cart/add respondió ' + response.status + ' ' + text);
          });
      }
    });
  };

  proto._fetchCart = function () {
    return fetch(this._route('cart', '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then(function (response) {
      if (!response.ok) throw new Error('aiko-payment-choice: cart.js respondió ' + response.status);
      return response.json();
    });
  };

  proto._changeLine = function (key, quantity, properties) {
    var payload = properties
      ? { id: key, quantity: quantity, properties: properties }
      : { id: key, quantity: quantity };

    return fetch(this._route('change', '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (response) {
      if (!response.ok) throw new Error('aiko-payment-choice: cart/change respondió ' + response.status);
      return response.json();
    });
  };

  /*
   * Escribe la propiedad del medio de pago en TODAS las líneas del carrito.
   *
   * La key de una línea se deriva de sus propiedades, así que una línea modificada vuelve con una
   * key nueva y puede fusionarse con otra línea. Eso descarta recorrer una lista fija de keys: se
   * relee el carrito en cada pasada y se marca cuáles ya quedaron resueltas.
   */
  proto._markEveryLine = function (value) {
    var self = this;
    var settled = {};
    var pass = 0;

    function step() {
      if (pass++ >= MAX_LINE_PASSES) {
        throw new Error('aiko-payment-choice: no se pudieron marcar todas las líneas');
      }

      return self._fetchCart().then(function (cart) {
        var item = null;
        var i;
        for (i = 0; i < cart.items.length; i++) {
          if (!settled[cart.items[i].key]) {
            item = cart.items[i];
            break;
          }
        }

        if (!item) return;

        settled[item.key] = true;

        var keysBefore = {};
        for (i = 0; i < cart.items.length; i++) keysBefore[cart.items[i].key] = true;

        var properties = Object.assign({}, item.properties || {});
        properties[PROPERTY] = value;

        return self._changeLine(item.key, item.quantity, properties).then(function (updated) {
          for (var j = 0; j < updated.items.length; j++) {
            if (!keysBefore[updated.items[j].key]) settled[updated.items[j].key] = true;
          }
          return step();
        });
      });
    }

    return Promise.resolve().then(step);
  };

  /*
   * Saca la propiedad del medio de pago de TODAS las líneas.
   *
   * `/cart/change.js` NO puede borrar una propiedad: escribir string vacío deja la clave con valor
   * en blanco, y un objeto de propiedades vacío se ignora directamente. Así que cada línea marcada
   * se BORRA y se vuelve a agregar sin la propiedad, lo que la fusiona de nuevo con la línea limpia
   * equivalente si existe. Borrar antes de re-agregar evita que un reintento fallido infle la
   * cantidad.
   *
   * Esto es el corazón de la verificación clave: un ítem cargado por transferencia que después
   * pasa por el botón de tarjeta tiene que llegar al checkout SIN `_medio_pago`.
   */
  proto._clearEveryLine = function () {
    var self = this;
    var pass = 0;

    function step() {
      if (pass++ >= MAX_LINE_PASSES) {
        throw new Error('aiko-payment-choice: no se pudieron limpiar todas las líneas');
      }

      return self._fetchCart().then(function (cart) {
        var item = null;
        for (var i = 0; i < cart.items.length; i++) {
          var props = cart.items[i].properties || {};
          if (Object.prototype.hasOwnProperty.call(props, PROPERTY)) {
            item = cart.items[i];
            break;
          }
        }

        if (!item) return;

        var properties = Object.assign({}, item.properties);
        delete properties[PROPERTY];

        return self
          ._changeLine(item.key, 0)
          .then(function () {
            return self._restoreLine(item, properties);
          })
          .then(step);
      });
    }

    return Promise.resolve().then(step);
  };

  /*
   * Vuelve a agregar una línea que se borró para sacarle la propiedad. Si esto falla, el carrito
   * de la clienta queda con un ítem menos, así que se reintenta antes de rendirse.
   */
  proto._restoreLine = function (item, properties) {
    var self = this;
    var payload = {
      id: item.variant_id,
      quantity: item.quantity,
      properties: properties,
    };

    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      payload.selling_plan = item.selling_plan_allocation.selling_plan.id;
    }

    var attempt = 0;

    function tryOnce() {
      attempt++;
      return fetch(self._route('add', '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then(function (response) {
        if (response.ok) return;
        if (attempt < RESTORE_ATTEMPTS) return tryOnce();
        throw new Error('aiko-payment-choice: no se pudo restaurar la variante ' + item.variant_id);
      });
    }

    return tryOnce();
  };

  /*
   * Saca el descuento por transferencia para que una clienta que cambia de idea no termine pagando
   * el precio de transferencia con tarjeta. Cualquier otro código que haya ingresado se conserva.
   */
  proto._dropTransferDiscount = function () {
    var self = this;
    var code = this._discountCode().toLowerCase();
    if (!code) return Promise.resolve();

    return this._fetchCart().then(function (cart) {
      var codes = (cart.discount_codes || []).map(function (discount) {
        return discount.code;
      });
      var remaining = codes.filter(function (existing) {
        return String(existing).toLowerCase() !== code;
      });

      if (remaining.length === codes.length) return;

      return fetch(self._route('update', '/cart/update') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: remaining.join(',') }),
      });
    });
  };

  /* ---------------------------------------------------------------- variantes */

  /*
   * Tres fuentes, porque hay tres arquitecturas de ficha conviviendo en este tema:
   *  1. `aiko:variant-change` — lo emiten `aiko-hero` y `aiko-cream-hero` en document.
   *  2. `change` burbujeando desde el form — cubre los selectores de Dawn y los radios del hero.
   *  3. Dawn re-renderiza la sección al cambiar de variante, así que el componente se vuelve a
   *     construir con los precios ya correctos desde Liquid y no hace falta hacer nada.
   *
   * Importante: esto solo sincroniza las ETIQUETAS. Lo que realmente se agrega al carrito sale de
   * `new FormData(form)` en el momento del clic, así que la variante correcta nunca depende de que
   * este sincronizado haya funcionado.
   */
  proto._bindVariantUpdates = function () {
    var self = this;

    document.addEventListener('aiko:variant-change', function (event) {
      var detail = event.detail || {};
      if (detail.id) self._renderPrices(String(detail.id));
      if (typeof detail.available === 'boolean' && !self._pending) {
        self._setDisabled(!detail.available);
      }
    });

    var form = this._form();
    if (form) {
      form.addEventListener('change', function () {
        var input = form.querySelector('[name="id"]');
        if (input && input.value) self._renderPrices(String(input.value));
      });
    }
  };

  proto._renderPrices = function (variantId) {
    var prices;
    try {
      prices = JSON.parse(this.getAttribute('data-prices') || '{}');
    } catch (error) {
      return;
    }

    var entry = prices[variantId];
    if (!entry) return;

    this.setAttribute('data-variant-id', variantId);
    if (this.cardPrice) this.cardPrice.textContent = entry.card;

    /* Sin descuento: el botón de transferencia muestra el precio de lista. */
    if (this.transferPrice) {
      this.transferPrice.textContent = this._isTransferOnly() ? entry.card : entry.transfer;
    }
  };

  if (!window.customElements.get('aiko-payment-choice')) {
    window.customElements.define('aiko-payment-choice', AikoPaymentChoice);
  }
})();
